import { useState, useEffect, useCallback } from "react";

const STOCK_REPORT_KEY = "stock_report";
const STRATEGY_REPORT_KEY = "strategy_report";
const SETTINGS_REPORT_KEY = "settings_report";

/**
 * Get the entire report object from localStorage
 */
const getReportObject = (
  storageKey: string,
): Record<string, unknown> => {
  try {
    const item = window.localStorage.getItem(storageKey);
    if (item === null) return {};
    const parsed = JSON.parse(item);
    // `JSON.parse` returns whatever was stored, and the annotation above is a
    // claim about it rather than a check of it. A truncated or hand-edited
    // entry can parse to null, to a number, or to an array, and indexing the
    // first of those throws before any figure is read.
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    return parsed;
  } catch (error) {
    console.warn(`Error reading localStorage key "${storageKey}":`, error);
    return {};
  }
};

/**
 * Read one report value out of its store, keeping only what matches the
 * `number | string` contract these hooks declare.
 *
 * Shared by all three hooks below: the validation has to hold for every report
 * surface, and a third copy of it is where one of them drifts.
 *
 * This checks the JSON TYPE, not that the value is a figure -- every string
 * survives, so "abc" still travels on and it is `toFiniteNumber` at the point
 * of use that decides whether it is a number. What is rejected here is what
 * could never be a figure in any reading: an object, an array, a boolean, a
 * null. In particular "" survives on purpose -- it is not corruption but the
 * sentinel the report layer writes for a figure the backend produced none of,
 * which the cards read as "no figure" and render as N/A.
 *
 * Read as an OWN property. The type check below is what actually makes a
 * polluted `Object.prototype` unreachable (every member of it is an object or
 * a function), but a lookup that cannot walk the chain at all does not depend
 * on that reasoning holding.
 */
const readReportValue = (
  storageKey: string,
  key: string,
  defaultValue: number | string,
): number | string => {
  if (!key) return defaultValue;

  const report = getReportObject(storageKey);
  if (!Object.prototype.hasOwnProperty.call(report, key)) {
    return defaultValue;
  }

  const stored = report[key];
  if (typeof stored === "number" || typeof stored === "string") {
    return stored;
  }
  return defaultValue;
};

/**
 * Set a value in the report object in localStorage
 */
const setReportValue = (
  storageKey: string,
  key: string,
  value: number | string,
): void => {
  try {
    const report = getReportObject(storageKey);
    report[key] = value;
    window.localStorage.setItem(storageKey, JSON.stringify(report));

    // Dispatch custom event to sync across components
    window.dispatchEvent(
      new CustomEvent("reportStorageChange", {
        detail: { storageKey, key, value },
      }),
    );
  } catch (error) {
    console.error(`Error setting localStorage key "${storageKey}":`, error);
  }
};

/**
 * Hook to manage stock report values in localStorage
 * @param key - The key within the stock_report object
 * @param defaultValue - Default value if key doesn't exist
 * @returns [value, setValue] - Current value and setter function
 */
export const useStockReportValue = (
  key: string,
  defaultValue: number | string,
): [number | string, (value: number | string) => void] => {
  const [storedValue, setStoredValue] = useState<number | string>(() =>
    readReportValue(STOCK_REPORT_KEY, key, defaultValue),
  );

  // Update localStorage when value changes
  const setValue = useCallback(
    (value: number | string) => {
      if (!key) return;

      setStoredValue(value);
      setReportValue(STOCK_REPORT_KEY, key, value);
    },
    [key],
  );

  // Listen for changes from other components
  useEffect(() => {
    if (!key) return;

    const handleStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        storageKey: string;
        key: string;
        value: number | string;
      }>;
      if (
        customEvent.detail.storageKey === STOCK_REPORT_KEY &&
        customEvent.detail.key === key
      ) {
        setStoredValue(customEvent.detail.value);
      }
    };

    window.addEventListener("reportStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("reportStorageChange", handleStorageChange);
    };
  }, [key]);

  return [storedValue, setValue];
};

/**
 * Hook to manage strategy report values in localStorage
 * @param key - The key within the strategy_report object
 * @param defaultValue - Default value if key doesn't exist
 * @returns [value, setValue] - Current value and setter function
 */
export const useStrategyReportValue = (
  key: string,
  defaultValue: number | string,
): [number | string, (value: number | string) => void] => {
  const [storedValue, setStoredValue] = useState<number | string>(() =>
    readReportValue(STRATEGY_REPORT_KEY, key, defaultValue),
  );

  // Update localStorage when value changes
  const setValue = useCallback(
    (value: number | string) => {
      if (!key) return;

      setStoredValue(value);
      setReportValue(STRATEGY_REPORT_KEY, key, value);
    },
    [key],
  );

  // Listen for changes from other components
  useEffect(() => {
    if (!key) return;

    const handleStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        storageKey: string;
        key: string;
        value: number | string;
      }>;
      if (
        customEvent.detail.storageKey === STRATEGY_REPORT_KEY &&
        customEvent.detail.key === key
      ) {
        setStoredValue(customEvent.detail.value);
      }
    };

    window.addEventListener("reportStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("reportStorageChange", handleStorageChange);
    };
  }, [key]);

  return [storedValue, setValue];
};

/**
 * Hook to manage settings report values in localStorage
 * @param key - The key within the settings_report object
 * @param defaultValue - Default value if key doesn't exist
 * @returns [value, setValue] - Current value and setter function
 */
export const useSettingsReportValue = (
  key: string,
  defaultValue: number | string,
): [number | string, (value: number | string) => void] => {
  const [storedValue, setStoredValue] = useState<number | string>(() =>
    readReportValue(SETTINGS_REPORT_KEY, key, defaultValue),
  );

  // Update localStorage when value changes
  const setValue = useCallback(
    (value: number | string) => {
      if (!key) return;

      setStoredValue(value);
      setReportValue(SETTINGS_REPORT_KEY, key, value);
    },
    [key],
  );

  // Listen for changes from other components
  useEffect(() => {
    if (!key) return;

    const handleStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        storageKey: string;
        key: string;
        value: number | string;
      }>;
      if (
        customEvent.detail.storageKey === SETTINGS_REPORT_KEY &&
        customEvent.detail.key === key
      ) {
        setStoredValue(customEvent.detail.value);
      }
    };

    window.addEventListener("reportStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("reportStorageChange", handleStorageChange);
    };
  }, [key]);

  return [storedValue, setValue];
};
